import { usePermissions } from '@refinedev/core'
import { Breadcrumb, Button, Card, Space, Table } from 'antd'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { gpuAdminConfigured } from '@/api/gpu/client'
import { ApiError } from '@/api/unwrapApiResult'
import { hasPermission } from '@/auth/capabilities'
import { RecordFallback } from '@/components/common/RecordFallback'

import { GpuState } from '../components/GpuState'
import { TerminateInstanceModal } from '../components/TerminateInstanceModal'
import { useTerminateGpuInstance } from '../hooks/useGpuMutations'
import { type GpuInstance, useGpuInstance } from '../hooks/useGpuOperations'
import { date, InstanceDetails } from './InstanceDetails'

type Task = GpuInstance['open_tasks'][number]

// status is a free string in the contract; unknown ones stay terminable.
const ENDED = ['ENDED', 'FAILED']

const taskColumns = [
  { title: 'Task', dataIndex: 'type' },
  { title: 'Status', dataIndex: 'status' },
  { title: 'Step', dataIndex: 'step', render: (s: string | null) => s ?? '—' },
  { title: 'Attempts', dataIndex: 'attempt_count', align: 'right' as const },
  {
    title: 'Next attempt',
    dataIndex: 'next_attempt_at',
    render: (value: string) => date(value),
  },
]

export default function GpuInstanceShowPage() {
  const { id = '' } = useParams<{ id: string }>()
  const { data, isLoading, error } = useGpuInstance(id)
  const notFound = error instanceof ApiError && error.statusCode === 404
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canTerminate =
    hasPermission(permissions, 'admin:gpu:write') &&
    Boolean(data) &&
    !ENDED.includes(data?.status ?? '')
  const terminate = useTerminateGpuInstance()
  const [terminating, setTerminating] = useState(false)

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: 'GPU' },
          { title: <Link to="/gpu/instances">Instances</Link> },
          { title: data?.name ?? id },
        ]}
      />
      <GpuState error={notFound ? null : error} />
      {!data && gpuAdminConfigured && (!error || notFound) && (
        <RecordFallback
          label="GPU instance"
          isLoading={isLoading}
          error={null}
        />
      )}
      {data && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card
            size="small"
            title={data.name}
            extra={
              canTerminate && (
                <Button
                  danger
                  size="small"
                  onClick={() => setTerminating(true)}
                >
                  Terminate
                </Button>
              )
            }
          >
            <InstanceDetails instance={data} />
          </Card>
          <Card size="small" title="Open tasks">
            <Table<Task>
              rowKey="id"
              dataSource={data.open_tasks}
              columns={taskColumns}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No open tasks' }}
            />
          </Card>
        </Space>
      )}
      {data && terminating && (
        <TerminateInstanceModal
          open
          name={data.name}
          loading={terminate.isPending}
          onSubmit={(reason, note) =>
            terminate.mutateAsync({ id: data.id, body: { reason, note } })
          }
          onClose={() => setTerminating(false)}
        />
      )}
    </div>
  )
}
